<template>
  <mpf-dialog
    v-model="isWarningModalVisible"
    type="modal"
    title="Your session will end soon"
    :strapline-text="`If you do not do anything in ${countdownHumanText} ${timeLeftUnits}, we will return you to the start and your progress will be lost`"
    :announce-while-open-ms="20000"
  >
    <template #actions>
      <mpf-button
        label="Stay on this page"
        class="mpf-u-margin-0"
        @click="inactivity.stayOnThisPage"
      />
      <mpf-button
        v-if="isNhsWebApp && !isOpenInNhsApp"
        label="Logout"
        variant="secondary"
        class="mpf-u-margin-0 mpf-u-sm-margin-left-2 mpf-u-margin-top-3 mpf-u-sm-margin-top-0"
        @click="handleNhsWebAppLogout()"
      />
    </template>
  </mpf-dialog>
  <MpfAppHeader
    ref="pageHeader"
    :is-open-in-nhs-app="isOpenInNhsApp"
    :show-header="!isOpenInNhsApp"
    :is-nhs-web-app="isNhsWebApp"
    :is-tenant-loaded="tenantStore.isLoaded"
    :tenant-id="tenantStore?.tenant?.tenantId"
    :tenant-name="tenantStore?.tenant?.localOrganisation?.name"
    :isLoading="apiLoading"
  />
  <div v-if="isOpenInNhsApp" class="mpf-nhs-app--supplier">
    <p class="mpf-u-padding-top-4 mpf-width-container mpf-u-margin-bottom-2">
      <strong>This service is provided by your GP surgery</strong>
    </p>
  </div>
  <div class="mpf-width-container">
    <div ref="pageBackButton">
      <!-- NB. NhsApp ue NHS app specific back actions -->
      <MpfAppBackButton
        :is-open-in-nhs-app="isOpenInNhsApp"
        :is-nhs-web-app="isNhsWebApp"
        :show-back-nhs-app="showBackNhsApp"
        :show-back-nhs-app-web="showBackNhsAppWeb"
        :show-back-button="showBackButton"
        :go-to-page="nhsAppActionsMeta?.goToPage"
        @go-to-page="handleNhsAppBack"
        :fallback-url="isNhsWebApp ? null : '/start'"
      />
    </div>

    <main class="mpf-main-wrapper" :class="[pageRouteName, pageHasBackButton]" ref="mainWrapper">
      <div v-if="notificationBanner?.text" class="mpf-u-margin-bottom-6">
        <MpfNotificationBanner
          v-if="notificationBanner?.text"
          :heading="notificationBanner.title"
          marginClass="mpf-u-margin-bottom-4"
        >
          <p>{{ notificationBanner.text }}</p>
        </MpfNotificationBanner>
      </div>
      <component :is="layoutComponent">
        <RouterView />
      </component>
    </main>
  </div>
  <div ref="pageFooter">
    <PageFooter
      :show-footer-links="isNhsWebApp || isOpenInNhsApp"
      :open-links-in-tab="!isOpenInNhsApp"
      :url-base="isOpenInNhsApp ? tenantId : ''"
      :tenant-name="tenant?.localOrganisation?.name"
      footer-text="© Crown Copyright"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';
  import { useRoute, RouterView } from 'vue-router';
  import { DefaultLayout } from './lib/layouts/layouts';
  import PageFooter from './lib/components/PageFooter.vue';
  import MpfDialog from './lib/components/MpfDialog/MpfDialog.vue';
  import MpfButton from './lib/components/MpfButton/MpfButton.vue';
  import { useViewportHeightMinus } from './lib/composables/useViewportHeightMinus';
  import { useSessionInactivity } from './lib/composables/useSessionInactivity';
  import { useSessionStore } from './stores/useSessionStore';
  import { router } from './router';
  import { storeToRefs } from 'pinia';
  import { getCountdownUnit } from './lib/helpers/time-format.helper';
  import { useTenantStore } from './stores/useTenantStore';
  import useErrorNavigation from './lib/composables/useErrorNavigation';
  import { useRequestStoresCommon } from './lib/composables/useRequestStoresCommon';
  import MpfNotificationBanner from './lib/components/MpfNotificationBanner/MpfNotificationBanner.vue';
  import { useRouteMetaOverrideStore } from './stores/useRouteMetaOverrideStore';
  import { getTenantFromPath } from './lib/composables/useTenantNavigation';
  import { useNhsAppStore } from './stores/useNhsAppStore';
  import { useNhsApp } from './lib/nhsapp/useNhsApp';
  import { usePageTitle } from '@/lib/composables/usePageTitle';
  import MpfAppBackButton from '@/lib/components/MpfAppBackButton/MpfAppBackButton.vue';
  import { AppPage } from '@/types/router-meta';
  import MpfAppHeader from '@/lib/MpfAppHeader/MpfAppHeader.vue';
  const errorNavigation = useErrorNavigation();
  const { endAll } = useRequestStoresCommon();

  const tenantStore = useTenantStore();
  const { tenantId, tenant } = storeToRefs(tenantStore);
  const nhsAppStore = useNhsAppStore();
  const nhsApp = useNhsApp();
  const route = useRoute();
  const metaOverrideStore = useRouteMetaOverrideStore();

  // Always pick the current route's layoutComponent, fallback to DefaultLayout
  const layoutComponent = computed(() => {
    return (route?.meta.layoutComponent as any) || DefaultLayout;
  });

  const nhsAppActionsMeta = computed(() => {
    return route?.meta.nhsAppActions as any;
  });

  // NB. exclude NHS apps logic
  const showBackButton = computed<boolean>(() => {
    if (metaOverrideBackButton.value !== undefined) {
      // NB. if not undefined we've set TRUE or FALSE
      return metaOverrideBackButton.value;
    }
    // default meta
    return route?.meta?.backButton === true;
  });

  // NHS apps meta logic
  const nhsAppMetaActions = computed(() => {
    const matched = route.matched;
    for (let i = matched.length - 1; i >= 0; i--) {
      const actions = matched[i]?.meta?.nhsAppActions;
      if (actions) return actions;
    }

    return undefined;
  });
  const showBackNhsApp = computed(() => nhsAppMetaActions.value?.showBackNhsApp);
  const showBackNhsAppWeb = computed(() => nhsAppMetaActions.value?.showBackNhsAppWeb);

  const { isOpenInNhsApp, isNhsWebApp } = storeToRefs(nhsAppStore);
  const { notificationBanner, backButton: metaOverrideBackButton } = storeToRefs(metaOverrideStore);
  const { apiLoading } = storeToRefs(tenantStore);

  const isWarningOpen = ref(false);
  const pageRouteName = computed(() => route.name);

  const pageHasBackButton = computed(() => (route.meta.backButton ? 'has-back-button' : ''));

  const pageHeader = ref<HTMLElement | null>(null);
  const pageBackButton = ref<HTMLElement | null>(null);
  const pageFooter = ref<HTMLElement | null>(null); // now DOM element
  const mainWrapper = ref<HTMLElement | null>(null);

  const VITE_ENV = import.meta.env.VITE_ENV?.toUpperCase() || 'DEVELOPMENT';

  onMounted(async () => {
    usePageTitle();
    const tenantId = getTenantFromPath(route.path);
    if (VITE_ENV !== 'PRODUCTION' && VITE_ENV !== 'PROD') {
      console.info('onMounted Tenant ID from URL:', tenantId);
    }
    await tenantStore.fetchTenant(tenantId);

    if (import.meta.env.DEV) {
      (window as any).__forceSessionTimeout = (ms: number) => {
        forceSessionTimeout(ms);
      };
      // NB. call from console with window.__forceSessionTimeout(60000 * 9.8)
    }
  });

  watch(
    () => route.fullPath,
    async (p) => {
      // TODO: NB Nhs App wide route meta uses. Remove once tested
      if (isOpenInNhsApp.value || isNhsWebApp.value) {
        console.log(
          'watch route.fullPath nhsAppMetaActions',
          isOpenInNhsApp.value,
          isNhsWebApp.value,
        );
        console.log(nhsAppMetaActions.value);
      }
      let backAction = () => nhsApp.goToPage(nhsAppMetaActions.value?.setBackAction);
      if (isOpenInNhsApp.value && nhsAppMetaActions.value?.setBackAction !== 'PP_GO_BACK') {
        console.log('nhsApp.setBackAction', nhsAppMetaActions.value?.setBackAction);
        nhsApp.setBackAction(backAction, () => console.log('backaction fallback'));
      }

      // NB. load tenant
      const tenantId = getTenantFromPath(p);
      if (VITE_ENV !== 'PRODUCTION' && VITE_ENV !== 'PROD') {
        console.info('watch Tenant ID from URL:', tenantId);
      }
      await tenantStore.fetchTenant(tenantId);
    },
  );

  // session timers
  const session = useSessionStore();
  const sessionStore = storeToRefs(session);
  const { countdownHumanText, isWarningModalVisible } = sessionStore;

  const inactivity = useSessionInactivity({
    store: session,
    router,
    countdownEnabled: true,
    ignoreSelectors: ['.mpf-dialog'],
    sessionEndedPath: '/session/expired',
    closeWarningUi: () => {
      isWarningOpen.value = false;
    },
    clearAppSessionData: async () => await handleEndSession(),
  });

  const handleEndSession = async () => {
    console.log('handleEndSession', isOpenInNhsApp.value);
    if (isOpenInNhsApp.value) {
      logout();
      nhsApp.goToHomepage();
      nhsApp.goToPage('HOME_PAGE');
      return;
    }
    await logout();
  };

  const handleNhsWebAppLogout = async () => {
    inactivity.stop();
    await logout();
    await router.push('page/you-have-logged-out');
  };

  const logout = async () => {
    await endAll('timeout');
    const result = await session.endSession();
    if (!result.ok) {
      await errorNavigation.navigateToError({
        message: 'Could not logout and end the session',
        errors: result.errors,
      });
      return false;
    }
    return true;
  };

  function handleNhsAppBack(goToPage: AppPage) {
    console.log('handleNhsAppBack', goToPage);
    if (goToPage === 'PP_GO_BACK') {
      return;
    }
    nhsApp?.goToPage(goToPage, () => console.log('Fallback for NHS goToPage', goToPage));
  }

  // NB. so we can call session functions elsewhere
  provide('sessionInactivity', inactivity);

  // NB. for testing session dialog
  const forceSessionTimeout = (ms) => {
    session.startSession(Date.now());
    inactivity.start();
    inactivity.setLastActivityAgo(ms);
  };
  const timeLeftUnits = computed(() => getCountdownUnit(session.countdownWarningMsTick));

  onBeforeUnmount(() => {
    if (import.meta.env.DEV) {
      delete (window as any).__setInactivityLastAgo;
    }
  });

  useViewportHeightMinus({
    main: mainWrapper,
    minus: [
      { label: 'header', ref: pageHeader },
      { label: 'backButton', ref: pageBackButton },
      { label: 'footer', ref: pageFooter },
    ],
    debug: VITE_ENV !== 'PRODUCTION',
    useDynamicViewport: true,
  });

  // TODO: theme tester selecting and switcher
  // document.documentElement.setAttribute('data-theme', 'medicus');
  // document.documentElement.setAttribute('data-theme', 'nhs');
</script>
<style lang="scss" scoped>
  .mpf-nhs-app--supplier {
    border-bottom: 3px solid #b1b4b6;
  }
</style>
